package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.model.AttendanceStatus
import ru.chm.gradereport.model.StudentDto
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.TabChip
import ru.chm.gradereport.ui.components.softClickable
import ru.chm.gradereport.platform.copyTextToClipboard
import ru.chm.gradereport.ui.theme.GradeTheme

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun SheetFilterPanel(
    filters: SheetFilterState,
    maxGrade: Int,
    visibleCount: Int,
    totalCount: Int,
    visibleStudents: List<StudentDto>,
    showAttention: Boolean,
    onChange: (SheetFilterState) -> Unit,
) {
    val colors = GradeTheme.colors
    val wash = if (colors.isDark) Color.White else Color.Black
    var expanded by remember { mutableStateOf(false) }
    var copied by remember { mutableStateOf(false) }
    val summary = if (filters.isActive) {
        "Фильтр · показано $visibleCount из $totalCount"
    } else {
        "Фильтр студентов"
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 10.dp)
            .background(colors.card, RoundedCornerShape(12.dp))
            .border(1.dp, colors.border, RoundedCornerShape(12.dp))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.dp)
                .softClickable(
                    onClick = { expanded = !expanded },
                    highlight = wash,
                    shape = RoundedCornerShape(10.dp),
                    hoverAlpha = 0.06f,
                    pressAlpha = 0.1f,
                )
                .padding(horizontal = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                summary,
                color = colors.textPrimary,
                fontWeight = FontWeight.SemiBold,
                fontSize = 15.sp,
                modifier = Modifier.weight(1f).padding(end = 12.dp),
            )
            ExpandChevron(expanded = expanded, mutedColor = colors.textMuted)
        }

        if (expanded) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.Bottom,
            ) {
                AppTextField(
                    value = filters.nameQuery,
                    onValueChange = { onChange(filters.copy(nameQuery = it)) },
                    label = "ФИО",
                    modifier = Modifier.weight(1f),
                )
                if (filters.isActive) {
                    SecondaryButton(
                        text = "Сбросить",
                        onClick = { onChange(SheetFilterState()) },
                        modifier = Modifier.height(56.dp),
                    )
                }
            }

            if (showAttention) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SecondaryButton(
                        text = if (copied) "Скопировано" else "Скопировать список студентов",
                        onClick = {
                            copyTextToClipboard(
                                visibleStudents.joinToString(separator = "\n") { it.fullName },
                            )
                            copied = true
                        },
                    )
                    Text(
                        "Студентов в списке: $visibleCount",
                        color = colors.textMuted,
                        fontSize = 13.sp,
                    )
                }
            }

            FilterChipRow(label = "Оценки") {
                TabChip(
                    text = "Все",
                    selected = filters.grade == GradeFilter.All &&
                        filters.attention == AttentionFilter.All,
                    onClick = {
                        onChange(
                            filters.copy(grade = GradeFilter.All, attention = AttentionFilter.All),
                        )
                    },
                )
                TabChip(
                    text = "Есть",
                    selected = filters.grade == GradeFilter.HasAny,
                    onClick = {
                        onChange(
                            filters.copy(
                                grade = GradeFilter.HasAny,
                                attention = AttentionFilter.All,
                            ),
                        )
                    },
                )
                TabChip(
                    text = "Нет",
                    selected = filters.grade == GradeFilter.Missing,
                    onClick = {
                        onChange(
                            filters.copy(
                                grade = GradeFilter.Missing,
                                attention = AttentionFilter.All,
                            ),
                        )
                    },
                )
                if (showAttention) {
                    TabChip(
                        text = "Ждут проверки",
                        selected = filters.attention == AttentionFilter.UnseenSubmissions,
                        onClick = {
                            onChange(
                                filters.copy(
                                    attention = AttentionFilter.UnseenSubmissions,
                                    grade = GradeFilter.All,
                                ),
                            )
                        },
                    )
                    TabChip(
                        text = "Не сдали",
                        selected = filters.attention == AttentionFilter.NotSubmitted,
                        onClick = {
                            onChange(
                                filters.copy(
                                    attention = AttentionFilter.NotSubmitted,
                                    grade = GradeFilter.All,
                                ),
                            )
                        },
                    )
                }
                (1..maxGrade).forEach { score ->
                    TabChip(
                        text = score.toString(),
                        selected = filters.grade == GradeFilter.Score(score),
                        onClick = {
                            onChange(
                                filters.copy(
                                    grade = GradeFilter.Score(score),
                                    attention = AttentionFilter.All,
                                ),
                            )
                        },
                    )
                }
            }

            FilterChipRow(label = "Посещаемость") {
                TabChip(
                    text = "Все",
                    selected = filters.attendance == AttendanceFilter.All,
                    onClick = { onChange(filters.copy(attendance = AttendanceFilter.All)) },
                )
                TabChip(
                    text = "Без отметки",
                    selected = filters.attendance == AttendanceFilter.Missing,
                    onClick = { onChange(filters.copy(attendance = AttendanceFilter.Missing)) },
                )
                if (showAttention) {
                    TabChip(
                        text = "Самоотметки",
                        selected = filters.attendance == AttendanceFilter.Status(AttendanceStatus.PENDING),
                        onClick = {
                            onChange(
                                filters.copy(
                                    attendance = AttendanceFilter.Status(AttendanceStatus.PENDING),
                                ),
                            )
                        },
                    )
                }
                AllAttendanceStatuses.forEach { status ->
                    TabChip(
                        text = statusLabel(status),
                        selected = filters.attendance == AttendanceFilter.Status(status),
                        onClick = {
                            onChange(filters.copy(attendance = AttendanceFilter.Status(status)))
                        },
                    )
                }
            }

            Text(
                if (filters.isActive) {
                    "Показано $visibleCount из $totalCount"
                } else {
                    "Студентов: $totalCount"
                },
                color = colors.textMuted,
                fontSize = 13.sp,
            )
        }
    }
}

@Composable
private fun ExpandChevron(expanded: Boolean, mutedColor: Color) {
    Box(
        modifier = Modifier.size(24.dp),
        contentAlignment = Alignment.Center,
    ) {
        Canvas(modifier = Modifier.size(18.dp)) {
            val stroke = 2.5.dp.toPx()
            val w = size.width
            val h = size.height
            val midX = w / 2f
            if (expanded) {
                drawLine(
                    color = mutedColor,
                    start = Offset(w * 0.2f, h * 0.62f),
                    end = Offset(midX, h * 0.32f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
                drawLine(
                    color = mutedColor,
                    start = Offset(midX, h * 0.32f),
                    end = Offset(w * 0.8f, h * 0.62f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
            } else {
                drawLine(
                    color = mutedColor,
                    start = Offset(w * 0.2f, h * 0.38f),
                    end = Offset(midX, h * 0.68f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
                drawLine(
                    color = mutedColor,
                    start = Offset(midX, h * 0.68f),
                    end = Offset(w * 0.8f, h * 0.38f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FilterChipRow(
    label: String,
    content: @Composable () -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = GradeTheme.colors.textPrimary, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            content()
        }
    }
}
